import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { parseEBUScoreCSV } from '../utils/ebuParser';
import { parsePBN } from '../utils/pbnParser';
import { parseUSEBIO, parseUSEBIODate } from '../utils/usebioParser';
import JSZip from 'jszip';
import './BulkUpload.css';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'bridge2026';

function parseDate(dateStr) {
  // EBUScore format: DD/MM/YYYY
  if (!dateStr) return new Date().toISOString().split('T')[0];
  
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return new Date().toISOString().split('T')[0];
}

function BulkUpload() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [pairingsFile, setPairingsFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState([]);
  const [importResults, setImportResults] = useState([]);

  function handleLogin(e) {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setMessage('');
    } else {
      setMessage('Incorrect password');
    }
  }

  async function handlePreview(e) {
    e.preventDefault();
    
    if (!zipFile || !pairingsFile) {
      setMessage('Please select both ZIP file and pairings CSV');
      return;
    }

    setMessage('Processing files...');
    
    try {
      // Read pairings CSV
      const pairingsText = await pairingsFile.text();
      const pairingsLines = pairingsText.split('\n').slice(1); // Skip header
      const pairings = [];
      
      for (const line of pairingsLines) {
        if (line.trim()) {
          const [csvFile, pbnFile] = line.split(',').map(s => s.trim());
          if (csvFile && pbnFile) {
            pairings.push({ csvFile, pbnFile });
          }
        }
      }

      // Read ZIP file
      const zip = await JSZip.loadAsync(zipFile);
      const previewData = [];
      
      for (const pairing of pairings) {
        try {
          // Find result file in zip (CSV or XML)
          const csvPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.csvFile)
          );

          // Find PBN in zip
          const pbnPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.pbnFile)
          );

          if (!csvPath) {
            previewData.push({
              csvFile: pairing.csvFile,
              pbnFile: pairing.pbnFile,
              status: 'error',
              error: 'Result file not found in ZIP'
            });
            continue;
          }

          const fileText = await zip.files[csvPath].async('text');
          const fileName = pairing.csvFile.toLowerCase();

          // Parse based on file type
          let parsedData;
          let boardCount = 0;

          if (fileName.endsWith('.xml')) {
            // Parse XML (USEBIO format)
            parsedData = parseUSEBIO(fileText);
            boardCount = Object.keys(parsedData.travellers).length;
          } else {
            // Parse CSV (EBUScore format)
            parsedData = parseEBUScoreCSV(fileText);

            // Parse PBN if exists
            if (pbnPath) {
              const pbnText = await zip.files[pbnPath].async('text');
              const handRecords = parsePBN(pbnText);
              boardCount = Object.keys(handRecords).length;
            } else {
              boardCount = Object.keys(parsedData.travellers).length;
            }
          }

          // Extract title and date from metadata
          const title = parsedData.metadata.Title || 'Untitled Game';
          const gameName = title;
          const gameDate = fileName.endsWith('.xml')
            ? parseUSEBIODate(parsedData.metadata.Date)
            : parseDate(parsedData.metadata.Date);
          
          previewData.push({
            csvFile: pairing.csvFile,
            pbnFile: pairing.pbnFile,
            status: 'ready',
            gameName: gameName,
            gameType: parsedData.gameType,
            date: gameDate,
            players: parsedData.scores.length,
            boards: boardCount || Object.keys(parsedData.travellers).length,
            _parsedData: parsedData  // Store for later use in import
          });
        } catch (error) {
          previewData.push({
            csvFile: pairing.csvFile,
            pbnFile: pairing.pbnFile,
            status: 'error',
            error: error.message
          });
        }
      }
      
      setPreview(previewData);
      setMessage(`Found ${previewData.length} games. Ready to import.`);
    } catch (error) {
      setMessage(`Error processing files: ${error.message}`);
    }
  }

  async function handleBulkImport() {
    if (preview.length === 0) {
      setMessage('Please preview files first');
      return;
    }

    setUploading(true);
    setMessage('Starting bulk import...');
    setImportResults([]);
    
    const results = [];
    
    try {
      // Read ZIP and pairings again
      const zip = await JSZip.loadAsync(zipFile);
      const pairingsText = await pairingsFile.text();
      const pairingsLines = pairingsText.split('\n').slice(1);
      const pairings = [];
      
      for (const line of pairingsLines) {
        if (line.trim()) {
          const [csvFile, pbnFile] = line.split(',').map(s => s.trim());
          if (csvFile && pbnFile) {
            pairings.push({ csvFile, pbnFile });
          }
        }
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < pairings.length; i++) {
        const pairing = pairings[i];
        setMessage(`Importing ${i + 1}/${pairings.length}: ${pairing.csvFile}...`);
        
        try {
          // Find files in zip
          const csvPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.csvFile)
          );
          const pbnPath = Object.keys(zip.files).find(path =>
            path.endsWith(pairing.pbnFile)
          );

          if (!csvPath) {
            results.push({
              csvFile: pairing.csvFile,
              status: 'error',
              error: 'Result file not found'
            });
            errorCount++;
            continue;
          }

          const fileText = await zip.files[csvPath].async('text');
          const fileName = pairing.csvFile.toLowerCase();

          // Parse based on file type
          let parsedData;
          let handRecords = null;

          if (fileName.endsWith('.xml')) {
            // Parse XML
            parsedData = parseUSEBIO(fileText);
            handRecords = parsedData.handRecords || null;
          } else {
            // Parse CSV
            parsedData = parseEBUScoreCSV(fileText);

            // Parse PBN if exists
            if (pbnPath) {
              const pbnText = await zip.files[pbnPath].async('text');
              handRecords = parsePBN(pbnText);
            }
          }

          // Extract title and date from metadata
          const title = parsedData.metadata.Title || 'Untitled Game';
          const gameName = title;
          const gameDate = fileName.endsWith('.xml')
            ? parseUSEBIODate(parsedData.metadata.Date)
            : parseDate(parsedData.metadata.Date);
          const numBoards = parseInt(parsedData.metadata.Boards) || 0;
          const director = parsedData.metadata.Director?.trim() || null;
          
          // Prepare game data
          const gameData = {
            title: gameName,
            game_date: gameDate,
            game_type: parsedData.gameType,
            num_boards: numBoards,
            director: director,
            scores: parsedData.scores,
            travellers: parsedData.travellers,
            hand_records: handRecords,
            metadata: parsedData.metadata
          };
          
          // Check if game already exists (by title AND date)
          const { data: existing } = await supabase
            .from('games')
            .select('id')
            .eq('title', gameName)
            .eq('game_date', gameDate)
            .single();

          if (existing) {
            // Update existing game
            const { error } = await supabase
              .from('games')
              .update({
                game_date: gameDate,
                game_type: parsedData.gameType,
                num_boards: numBoards,
                director: director,
                scores: parsedData.scores,
                travellers: parsedData.travellers,
                hand_records: handRecords,
                metadata: parsedData.metadata
              })
              .eq('id', existing.id);
            
            if (error) throw error;

            results.push({
              csvFile: pairing.csvFile,
              status: 'updated',
              gameName: gameName
            });
          } else {
            // Insert new game
            const { error } = await supabase
              .from('games')
              .insert([gameData])
              .select('id')
              .single();

            if (error) throw error;
            
            results.push({
              csvFile: pairing.csvFile,
              status: 'created',
              gameName: gameName
            });
          }
          
          successCount++;
        } catch (error) {
          results.push({
            csvFile: pairing.csvFile,
            status: 'error',
            error: error.message
          });
          errorCount++;
        }
        
        setImportResults([...results]);
      }
      
      setMessage(`Import complete! ${successCount} succeeded, ${errorCount} failed.`);
    } catch (error) {
      setMessage(`Bulk import error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  if (!authenticated) {
    return (
      <div className="admin-upload">
        <h2>Bulk Admin Upload</h2>
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">Login</button>
        </form>
        {message && <p className="message error">{message}</p>}
      </div>
    );
  }

  return (
    <div className="bulk-upload">
      <h2>Bulk Upload Games</h2>
      
      <div className="upload-section">
        <h3>Step 1: Select Files</h3>
        <div className="file-inputs">
          <div className="file-input-group">
            <label>ZIP File (containing CSVs/XMLs and PBNs):</label>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setZipFile(e.target.files[0])}
            />
            {zipFile && <span className="file-name">{zipFile.name}</span>}
          </div>

          <div className="file-input-group">
            <label>Pairings CSV (Result_File,PBN_File - PBN optional for XML):</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setPairingsFile(e.target.files[0])}
            />
            {pairingsFile && <span className="file-name">{pairingsFile.name}</span>}
          </div>
        </div>
        
        <button 
          onClick={handlePreview} 
          disabled={!zipFile || !pairingsFile || uploading}
          className="preview-btn"
        >
          Preview Games
        </button>
      </div>
      
      {preview.length > 0 && (
        <div className="preview-section">
          <h3>Step 2: Preview ({preview.length} games)</h3>
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>CSV File</th>
                  <th>PBN File</th>
                  <th>Game Name</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Players</th>
                  <th>Boards</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((item, idx) => (
                  <tr key={idx} className={item.status}>
                    <td>{item.csvFile}</td>
                    <td>{item.pbnFile}</td>
                    <td>{item.gameName || '-'}</td>
                    <td>{item.gameType || '-'}</td>
                    <td>{item.date || '-'}</td>
                    <td>{item.players || '-'}</td>
                    <td>{item.boards || '-'}</td>
                    <td>
                      {item.status === 'ready' && <span className="status-ready">✓ Ready</span>}
                      {item.status === 'error' && <span className="status-error">✗ {item.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <button 
            onClick={handleBulkImport} 
            disabled={uploading || preview.filter(p => p.status === 'ready').length === 0}
            className="import-btn"
          >
            {uploading ? 'Importing...' : `Import ${preview.filter(p => p.status === 'ready').length} Games`}
          </button>
        </div>
      )}
      
      {importResults.length > 0 && (
        <div className="results-section">
          <h3>Import Results</h3>
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>CSV File</th>
                  <th>Game Name</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {importResults.map((result, idx) => (
                  <tr key={idx} className={result.status}>
                    <td>{result.csvFile}</td>
                    <td>{result.gameName || '-'}</td>
                    <td>
                      {result.status === 'created' && <span className="status-created">✓ Created</span>}
                      {result.status === 'updated' && <span className="status-updated">↻ Updated</span>}
                      {result.status === 'error' && <span className="status-error">✗ {result.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {message && <p className="message">{message}</p>}
    </div>
  );
}

export default BulkUpload;
